/**
 * Global Asset Types Page (Admin Only)
 * Manage asset types and subtypes across the system.
 * 
 * Supports icon and color configuration at both:
 * - Asset Type level (parent) - used on main map and reports
 * - Subtype level (child) - used in detail views and reports with finer-grained iconography
 * 
 * Modal-based editing pattern (per Mark's preference - no inline editing that pushes content down)
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';

import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { fetchAssetTypes, fetchAssetSubTypes } from '../../../store/slices/assetSlice';
import AppBar from '../../elements/AppBar';
import PageBody from '../../elements/PageBody';
import PageTitle from '../../elements/PageTitle';
import SpriteIcon from '../../elements/SpriteIcon';
import { IconPickerButton } from '../../elements/IconPicker';
import { ColorPickerButton, ColorSwatch } from '../../elements/ColorPicker';
import { getAssetTypeShapeBorderRadiusMui, getAssetTypeShape } from '../../../utils/assetShapeLanguage';
import { getSubtypeIconWithFallback, getSubtypeColorWithFallback } from '../../../utils/assetTypeResolution';
import useProjectSelector from '../../../hooks/useProjectSelector';
import * as api from '../../../services/api';

const GlobalAssetTypesPage = () => {
  const dispatch = useAppDispatch();
  const { assetTypes: assetTypeList, assetSubTypes: assetSubtypeList, loading } = useAppSelector((state) => state.asset);
  const assetTypes = assetTypeList || [];
  const assetSubtypes = assetSubtypeList || [];
  const { ProjectSelectorComponent } = useProjectSelector();
  
  // Type modal state
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeFormData, setTypeFormData] = useState({
    name: '',
    description: '',
    trackingRequired: true,
    color: '#EC4F36',
    iconName: '',
  });
  
  // Subtype modal state
  const [subtypeModalOpen, setSubtypeModalOpen] = useState(false);
  const [editingSubtype, setEditingSubtype] = useState(null);
  const [subtypeParentTypeId, setSubtypeParentTypeId] = useState(null);
  const [subtypeFormData, setSubtypeFormData] = useState({
    name: '',
    description: '',
    dimensions: '',
    category: '',
    iconName: '',  // Optional - falls back to parent if not set
    color: '',     // Optional - falls back to parent if not set
  });
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'type' or 'subtype'
  
  // Form state
  const [formLoading, setFormLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    dispatch(fetchAssetTypes());
    dispatch(fetchAssetSubTypes());
  }, [dispatch]);

  // Refresh data after create/update/delete
  const handleRefresh = () => {
    dispatch(fetchAssetTypes());
    dispatch(fetchAssetSubTypes());
  };

  // Group subtypes by type
  const subtypesByType = assetSubtypes.reduce((acc, subtype) => {
    const typeId = subtype.typeId;
    if (!acc[typeId]) acc[typeId] = [];
    acc[typeId].push(subtype);
    return acc;
  }, {});

  // Type Modal handlers
  const handleOpenAddTypeModal = () => {
    setEditingType(null);
    setTypeFormData({
      name: '',
      description: '',
      trackingRequired: true,
      color: '#EC4F36',
      iconName: '',
    });
    setTypeModalOpen(true);
  };

  const handleOpenEditTypeModal = (type) => {
    setEditingType(type);
    setTypeFormData({
      name: type.name || '',
      description: type.description || '',
      trackingRequired: type.trackingRequired !== false,
      color: type.color || '#EC4F36',
      iconName: type.iconName || '',
    });
    setTypeModalOpen(true);
  };

  const handleCloseTypeModal = () => {
    setTypeModalOpen(false);
    setEditingType(null);
  };

  const handleTypeFormChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setTypeFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Direct value setter for pickers (icon, color)
  const handleTypeFormSet = (field, value) => {
    setTypeFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitType = async () => {
    if (!typeFormData.name.trim()) {
      setSnackbar({ open: true, message: 'Name is required', severity: 'error' });
      return;
    }
    
    setFormLoading(true);
    
    try {
      if (editingType) {
        await api.updateAssetType(editingType.id, typeFormData);
        setSnackbar({ open: true, message: 'Asset type updated successfully', severity: 'success' });
      } else {
        await api.createAssetType(typeFormData);
        setSnackbar({ open: true, message: 'Asset type created successfully', severity: 'success' });
      }
      
      handleCloseTypeModal();
      handleRefresh();
    } catch (err) {
      console.error('Error saving asset type:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to save asset type', severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  // Subtype Modal handlers
  const handleOpenAddSubtypeModal = (typeId) => {
    setEditingSubtype(null);
    setSubtypeParentTypeId(typeId);
    setSubtypeFormData({
      name: '',
      description: '',
      dimensions: '',
      category: '',
      iconName: '',  // Optional - will fall back to parent type
      color: '',     // Optional - will fall back to parent type
    });
    setSubtypeModalOpen(true);
  };

  const handleOpenEditSubtypeModal = (subtype) => {
    setEditingSubtype(subtype);
    setSubtypeParentTypeId(subtype.typeId);
    setSubtypeFormData({
      name: subtype.name || '',
      description: subtype.description || '',
      dimensions: subtype.dimensions || '',
      category: subtype.category || '',
      iconName: subtype.iconName || '',  // Preserve existing icon
      color: subtype.color || '',        // Preserve existing color
    });
    setSubtypeModalOpen(true);
  };

  const handleCloseSubtypeModal = () => {
    setSubtypeModalOpen(false);
    setEditingSubtype(null);
    setSubtypeParentTypeId(null);
  };

  const handleSubtypeFormChange = (field) => (event) => {
    setSubtypeFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // Direct value setter for pickers (icon, color)
  const handleSubtypeFormSet = (field, value) => {
    setSubtypeFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Get the parent type for subtype form (for fallback display)
  const getParentTypeForSubtype = () => {
    return assetTypes.find((t) => t.id === subtypeParentTypeId) || {};
  };

  const handleSubmitSubtype = async () => {
    if (!subtypeFormData.name.trim()) {
      setSnackbar({ open: true, message: 'Name is required', severity: 'error' });
      return;
    }
    
    setFormLoading(true);
    
    try {
      const data = {
        ...subtypeFormData,
        typeId: subtypeParentTypeId,
      };
      
      if (editingSubtype) {
        await api.updateAssetSubtype(editingSubtype.id, data);
        setSnackbar({ open: true, message: 'Subtype updated successfully', severity: 'success' });
      } else {
        await api.createAssetSubtype(data);
        setSnackbar({ open: true, message: 'Subtype created successfully', severity: 'success' });
      }
      
      handleCloseSubtypeModal();
      handleRefresh();
    } catch (err) {
      console.error('Error saving subtype:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to save subtype', severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  // Delete handlers
  const handleOpenDeleteDialog = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    setFormLoading(true);
    
    try {
      if (deleteType === 'type') {
        await api.deleteAssetType(itemToDelete.id);
        setSnackbar({ open: true, message: 'Asset type deleted successfully', severity: 'success' });
      } else {
        await api.deleteAssetSubtype(itemToDelete.id);
        setSnackbar({ open: true, message: 'Subtype deleted successfully', severity: 'success' });
      }
      
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDeleteType(null);
      handleRefresh();
    } catch (err) {
      console.error('Error deleting:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to delete', severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />
      
      <PageBody>
        {/* Title Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '24px' }}>
          <PageTitle sx={{ mb: 0 }}>
            Asset Types Configuration
          </PageTitle>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddTypeModal}
              sx={{ height: 44 }}
            >
              Add Asset Type
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {assetTypes.map((type) => {
              const typeColor = type.color || '#EC4F36';
              return (
                <Grid item xs={12} md={6} lg={4} key={type.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'box-shadow 0.2s ease',
                      '&:hover': {
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => handleOpenEditTypeModal(type)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {/* Icon with color background using shape language */}
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: getAssetTypeShapeBorderRadiusMui(type),
                            backgroundColor: typeColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {type.iconName ? (
                            <SpriteIcon iconName={type.iconName} size={28} />
                          ) : (
                            <CategoryIcon sx={{ color: 'white', fontSize: 24 }} />
                          )}
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                            {type.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* Color swatch */}
                            <ColorSwatch color={typeColor} size={12} />
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {typeColor}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip 
                          label={type.trackingRequired !== false ? 'Tracked' : 'Untracked'}
                          size="small"
                          color={type.trackingRequired !== false ? 'success' : 'default'}
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {type.description || 'No description'}
                    </Typography>

                    {/* Subtype count as a chip */}
                    <Chip
                      label={`${(subtypesByType[type.id] || []).length} subtypes`}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                    <IconButton 
                      size="small" 
                      title="Edit type"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditTypeModal(type);
                      }}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '28px',
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      title="Delete type" 
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDeleteDialog(type, 'type');
                      }}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '28px',
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
              );
            })}

            {assetTypes.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No asset types configured. Click &quot;Add Asset Type&quot; to create one.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </PageBody>

      {/* Type Modal - includes icon picker and subtypes list */}
      <Dialog open={typeModalOpen} onClose={handleCloseTypeModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingType ? 'Edit Asset Type' : 'Add Asset Type'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Icon and Color pickers side by side */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Icon & Color
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Icon
                  </Typography>
                  <IconPickerButton
                    iconName={typeFormData.iconName}
                    color={typeFormData.color}
                    shape={getAssetTypeShape({ deviceType: typeFormData.deviceType })}
                    onSelect={(icon) => handleTypeFormSet('iconName', icon)}
                    size={44}
                    label="Select Icon"
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Color
                  </Typography>
                  <ColorPickerButton
                    color={typeFormData.color}
                    onSelect={(color) => handleTypeFormSet('color', color)}
                    size={44}
                    label="Select Color"
                  />
                </Box>
                {/* Preview */}
                <Box sx={{ ml: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: getAssetTypeShapeBorderRadiusMui({ deviceType: typeFormData.deviceType }),
                      backgroundColor: typeFormData.color || '#EC4F36',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {typeFormData.iconName ? (
                      <SpriteIcon iconName={typeFormData.iconName} size={28} />
                    ) : (
                      <CategoryIcon sx={{ color: 'white', fontSize: 24 }} />
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Name"
                fullWidth
                required
                value={typeFormData.name}
                onChange={handleTypeFormChange('name')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={typeFormData.description}
                onChange={handleTypeFormChange('description')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={typeFormData.trackingRequired}
                    onChange={handleTypeFormChange('trackingRequired')}
                  />
                }
                label="Tracking Required"
              />
            </Grid>

            {/* Subtypes List (only shown when editing existing type) */}
            {editingType && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subtypes ({(subtypesByType[editingType.id] || []).length})
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      handleCloseTypeModal();
                      handleOpenAddSubtypeModal(editingType.id);
                    }}
                    sx={{ height: 36, borderRadius: '28px' }}
                  >
                    Add Subtype
                  </Button>
                </Box>
                <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {(subtypesByType[editingType.id] || []).map((subtype) => {
                    const effectiveIcon = getSubtypeIconWithFallback(subtype, editingType);
                    const effectiveColor = getSubtypeColorWithFallback(subtype, editingType);
                    const hasOwnIcon = !!subtype.iconName;
                    const hasOwnColor = !!subtype.color;
                    
                    return (
                      <ListItem
                        key={subtype.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 2,  // 8px bottom margin between subtypes
                          minHeight: 44,
                        }}
                      >
                        {/* Icon preview */}
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 0.5,
                            backgroundColor: effectiveColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1.5,
                            opacity: hasOwnIcon || hasOwnColor ? 1 : 0.7,
                          }}
                        >
                          <SpriteIcon iconName={effectiveIcon} size={18} />
                        </Box>
                        <ListItemText
                          primary={subtype.name}
                          secondary={
                            !hasOwnIcon && !hasOwnColor
                              ? 'Using parent settings'
                              : [
                                  hasOwnIcon && 'Custom icon',
                                  hasOwnColor && 'Custom color',
                                ]
                                  .filter(Boolean)
                                  .join(', ')
                          }
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleCloseTypeModal();
                              handleOpenEditSubtypeModal(subtype);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(subtype, 'subtype')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                  {(subtypesByType[editingType.id] || []).length === 0 && (
                    <ListItem>
                      <Typography variant="body2" color="text.secondary">
                        No subtypes defined. Click &quot;Add Subtype&quot; to create one.
                      </Typography>
                    </ListItem>
                  )}
                </List>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTypeModal} disabled={formLoading} sx={{ height: 44, borderRadius: '28px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitType}
            variant="contained"
            disabled={formLoading}
            sx={{ height: 44, borderRadius: '28px' }}
          >
            {formLoading ? <CircularProgress size={20} /> : (editingType ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subtype Modal - includes icon and color pickers with parent fallback */}
      <Dialog open={subtypeModalOpen} onClose={handleCloseSubtypeModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSubtype ? 'Edit Subtype' : 'Add Subtype'}
        </DialogTitle>
        <DialogContent>
          {(() => {
            const parentType = getParentTypeForSubtype();
            const effectiveIcon = subtypeFormData.iconName || parentType.iconName || 'Mat_Marker';
            const effectiveColor = subtypeFormData.color || parentType.color || '#EC4F36';
            
            return (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {/* Icon and Color pickers */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Icon & Color (optional - inherits from parent if not set)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Icon {!subtypeFormData.iconName && '(inherited)'}
                      </Typography>
                      <IconPickerButton
                        iconName={subtypeFormData.iconName}
                        color={effectiveColor}
                        shape={getAssetTypeShape(parentType)}
                        onSelect={(icon) => handleSubtypeFormSet('iconName', icon)}
                        size={44}
                        label="Select Icon"
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Color {!subtypeFormData.color && '(inherited)'}
                      </Typography>
                      <ColorPickerButton
                        color={subtypeFormData.color}
                        onSelect={(color) => handleSubtypeFormSet('color', color)}
                        size={44}
                        label="Select Color"
                      />
                    </Box>
                    {/* Preview showing effective values */}
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Preview
                      </Typography>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: getAssetTypeShapeBorderRadiusMui(parentType),
                          backgroundColor: effectiveColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: (subtypeFormData.iconName || subtypeFormData.color) ? 1 : 0.7,
                        }}
                      >
                        <SpriteIcon iconName={effectiveIcon} size={28} />
                      </Box>
                    </Box>
                  </Box>
                  {/* Parent type info */}
                  {parentType.name && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Parent type: {parentType.name}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    fullWidth
                    required
                    value={subtypeFormData.name}
                    onChange={handleSubtypeFormChange('name')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={subtypeFormData.description}
                    onChange={handleSubtypeFormChange('description')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Dimensions"
                    fullWidth
                    value={subtypeFormData.dimensions}
                    onChange={handleSubtypeFormChange('dimensions')}
                    placeholder="e.g., 8x14"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Category"
                    fullWidth
                    value={subtypeFormData.category}
                    onChange={handleSubtypeFormChange('category')}
                    placeholder="e.g., Heavy Duty"
                  />
                </Grid>
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubtypeModal} disabled={formLoading} sx={{ height: 44, borderRadius: '28px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitSubtype}
            variant="contained"
            disabled={formLoading}
            sx={{ height: 44, borderRadius: '28px' }}
          >
            {formLoading ? <CircularProgress size={20} /> : (editingSubtype ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete {deleteType === 'type' ? 'Asset Type' : 'Subtype'}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{itemToDelete?.name}&quot;?
            {deleteType === 'type' && ' This will not delete associated subtypes.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={formLoading}
            sx={{ height: 44, borderRadius: '28px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={formLoading}
            sx={{ height: 44, borderRadius: '28px' }}
          >
            {formLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GlobalAssetTypesPage;
